import { useEffect, useState, useCallback } from 'react';
import Sidebar from '../../../components/sidebar/Sidebar';
import '../ServerData.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:9090/api';

function getToken() { return localStorage.getItem('token'); }
function authHeaders() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` };
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

function isDue(r) {
  return r.status === 'pending' && new Date(r.dueAt).getTime() <= Date.now();
}

function ReminderCard({ reminder, onUpdate }) {
  const [open, setOpen]   = useState(false);
  const [saving, setSaving] = useState(false);

  const resolve = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/botdata/reminders/${reminder._id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ status: 'resolved', resolvedById: 'dashboard', resolvedByUsername: 'Dashboard' }),
      });
      onUpdate();
    } finally {
      setSaving(false);
    }
  };

  const overdue = isDue(reminder);
  const statusClass = overdue ? 'sd-badge-rejected' : `sd-badge-${reminder.status}`;

  return (
    <div className="sd-card">
      <div className="sd-card-header" onClick={() => setOpen(o => !o)}>
        <div className="sd-card-left">
          <div className="sd-card-meta">
            {reminder.botId && <span className="sd-bot-pill"><i className="bi bi-robot" />{reminder.botId}</span>}
            <span><i className="bi bi-person-check" /> {reminder.createdByUsername}</span>
            {reminder.memberUsername && <span><i className="bi bi-person" /> for {reminder.memberUsername}</span>}
            <span><i className="bi bi-building" /> {reminder.guildName || reminder.guildId}</span>
            <span style={{ color: overdue ? '#f87171' : '#475569' }}>
              <i className="bi bi-alarm" /> Due: {formatDate(reminder.dueAt)}
            </span>
            {reminder.overdueCount > 0 && (
              <span style={{ color: '#f87171' }}><i className="bi bi-exclamation-circle" /> {reminder.overdueCount}x overdue</span>
            )}
          </div>
          <div className="sd-card-content">{reminder.message}</div>
        </div>
        <div className="sd-card-actions">
          <span className={`sd-badge ${statusClass}`}>{overdue ? 'overdue' : reminder.status}</span>
          <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} style={{ color: '#475569', fontSize: '0.8rem' }} />
        </div>
      </div>

      {open && (
        <div className="sd-detail">
          <div className="sd-field-group">
            <div className="sd-field-label">Created</div>
            <div className="sd-field-value">{formatDate(reminder.createdAt)}</div>
          </div>
          {reminder.firedAt && (
            <div className="sd-field-group">
              <div className="sd-field-label">Fired At</div>
              <div className="sd-field-value">{formatDate(reminder.firedAt)}</div>
            </div>
          )}
          {reminder.resolvedAt && (
            <div className="sd-field-group">
              <div className="sd-field-label">Resolved</div>
              <div className="sd-field-value">{formatDate(reminder.resolvedAt)} by {reminder.resolvedByUsername || '—'}</div>
            </div>
          )}
          {reminder.status !== 'resolved' && (
            <div className="sd-action-row">
              <button className="sd-btn sd-btn-resolve" onClick={resolve} disabled={saving}>
                <i className="bi bi-check-circle" /> Mark Resolved
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function RemindersPage() {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = ['pending', 'fired', 'resolved'].includes(filter) ? `?status=${filter}` : '';
      const res = await fetch(`${API}/botdata/reminders${params}`, { headers: authHeaders() });
      const data = await res.json();
      setReminders(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="sd-page">
      <Sidebar title="Bot Reminders" />

      <div className="sd-header">
        <div className="sd-title">
          <i className="bi bi-alarm-fill" />
          Bot Reminders
        </div>
        <div className="sd-filters">
          {['all', 'pending', 'fired', 'resolved'].map(f => (
            <button key={f} className={`sd-filter-btn${filter === f ? ' active' : ''}`} onClick={() => setFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
          <button className="sd-filter-btn" onClick={load}><i className="bi bi-arrow-clockwise" /></button>
        </div>
      </div>

      <div className="sd-stats">
        {[
          { label: 'Total', value: reminders.length, color: '#e2e8f0' },
          { label: 'Pending', value: reminders.filter(r => r.status === 'pending').length, color: '#fbbf24' },
          { label: 'Overdue', value: reminders.filter(r => isDue(r)).length, color: '#f87171' },
          { label: 'Fired', value: reminders.filter(r => r.status === 'fired').length, color: '#facc15' },
          { label: 'Resolved', value: reminders.filter(r => r.status === 'resolved').length, color: '#4ade80' },
        ].map(s => (
          <div className="sd-stat-pill" key={s.label}>
            <div className="label">{s.label}</div>
            <div className="value" style={{ color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[...Array(4)].map((_, i) => <div key={i} className="sd-skel" style={{ height: 68, borderRadius: 14 }} />)}
        </div>
      ) : reminders.length === 0 ? (
        <div className="sd-empty"><i className="bi bi-alarm" /><p>No reminders found</p></div>
      ) : (
        reminders.map(r => <ReminderCard key={String(r._id)} reminder={r} onUpdate={load} />)
      )}
    </div>
  );
}
